<template>
    <BCard class="products-review-section mb-1">
        <template #header>
            <div class="products-review-section__heading">
                <div>
                    <h5 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):products', 'Products') }}
                    </h5>
                    <p class="text-less-muted mb-0">
                        {{ translate(
                            'Component(ResearchReviewProcess):products-description',
                            'Review extracted product facts and confirm their Chemity matches.',
                        ) }}
                    </p>
                    <small class="text-less-muted d-block mt-25">
                        {{ summaryLabel }}
                    </small>
                </div>
                <BButton
                    v-if="allowAddProduct"
                    type="button"
                    variant="outline-primary"
                    class="products-review-section__add"
                    @click="addProduct"
                >
                    <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-product', 'Add product') }}
                </BButton>
            </div>
        </template>

        <BAlert v-if="!viewModel.items.length" variant="light-secondary" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):no-products',
                    'No products were extracted from this document.',
                ) }}
            </div>
        </BAlert>

        <BAlert v-if="hydrationFailed" variant="light-warning" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):product-summaries-unavailable',
                    'Some product labels could not be loaded. Existing links are preserved.',
                ) }}
            </div>
        </BAlert>

        <BAlert v-if="viewModel.orphanedLinks.length" variant="light-warning" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):unassigned-product-links',
                    'Some existing product links cannot be assigned to one extracted row. They are preserved for review.',
                ) }}
            </div>
        </BAlert>

        <BAlert v-if="viewModel.duplicateProductIds.length" variant="light-warning" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):duplicate-product-links',
                    'Some Chemity products are linked more than once. Existing links are preserved but require review.',
                ) }}
            </div>
        </BAlert>

        <ProductReviewCard
            v-for="item in viewModel.items"
            :key="item.rowKey"
            :number="item.index + 1"
            :product-index="item.index"
            :product="item.product"
            :link="item.link"
            :summary="item.summary"
            :recommendation="recommendationFor(item)"
            :evaluation="item.evaluation"
            :product-lookup="productLookup"
            :excluded-product-ids="excludedProductIds(item.link)"
            :hydrating="isHydrating(item.link)"
            :violations="productViolations(item.index)"
            :allow-add-attribute="allowAddAttribute"
            @input="updateProduct(item.index, $event)"
            @select-link="selectLink(item, $event)"
            @remove="removeProduct(item.index)"
        />

        <div class="products-review-section__additional mt-2">
            <h6 class="mb-25">
                {{ translate(
                    'Component(ResearchReviewProcess):additional-covered-products',
                    'Additional covered products',
                ) }}
            </h6>
            <p class="text-less-muted small mb-1">
                {{ translate(
                    'Component(ResearchReviewProcess):additional-covered-products-description',
                    'Add Chemity products covered by the document without a corresponding extracted product row.',
                ) }}
            </p>

            <div v-if="availablePreferredSummary" class="products-review-section__preferred mb-1">
                <div>
                    <small class="text-less-muted d-block">
                        {{ translate(
                            'Component(ResearchReviewProcess):recommended-for-review',
                            'Recommended for this review',
                        ) }}
                    </small>
                    <span>{{ productSummaryLabel(availablePreferredSummary) }}</span>
                </div>
                <BButton
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    @click="addAdditionalLink(availablePreferredSummary)"
                >
                    {{ translate('Component(ResearchReviewProcess):add-coverage', 'Add coverage') }}
                </BButton>
            </div>

            <ProductLookupSelect
                :value="null"
                :lookup="productLookup"
                :excluded-product-ids="usedProductIds"
                :clearable="false"
                :placeholder="translate(
                    'Component(ResearchReviewProcess):search-additional-products',
                    'Search and add a covered product',
                )"
                @input="addAdditionalLink"
            />

            <div
                v-for="item in viewModel.additionalLinks"
                :key="`additional-product-link-${item.sourceIndex}`"
                class="products-review-section__link-row"
            >
                <div class="min-width-0">
                    <strong class="d-block text-truncate">
                        {{ productLinkLabel(item) }}
                    </strong>
                    <small class="text-less-muted">
                        {{ linkStatusLabel(item.link) }}
                    </small>
                    <small v-if="item.missingProduct" class="text-warning d-block">
                        {{ translate(
                            'Component(ResearchReviewProcess):product-unavailable',
                            'This product is no longer available.',
                        ) }}
                    </small>
                </div>
                <BButton
                    type="button"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    :title="translate('Component(Button):remove', 'Remove')"
                    @click="removeLink(item.sourceIndex)"
                >
                    <FeatherIcon icon="Trash2Icon" size="16" />
                </BButton>
            </div>
        </div>

        <div v-if="viewModel.orphanedLinks.length" class="products-review-section__orphaned mt-2">
            <h6 class="mb-25">
                {{ translate(
                    'Component(ResearchReviewProcess):product-links-requiring-review',
                    'Existing links requiring review',
                ) }}
            </h6>
            <div
                v-for="item in viewModel.orphanedLinks"
                :key="`orphaned-product-link-${item.sourceIndex}`"
                class="products-review-section__link-row"
            >
                <div class="min-width-0">
                    <strong class="d-block text-truncate">{{ productLinkLabel(item) }}</strong>
                    <small class="text-less-muted">
                        extractedProductId: {{ item.link.extractedProductId || '-' }}
                    </small>
                </div>
                <BButton
                    type="button"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    :title="translate('Component(Button):remove', 'Remove')"
                    @click="removeLink(item.sourceIndex)"
                >
                    <FeatherIcon icon="Trash2Icon" size="16" />
                </BButton>
            </div>
        </div>
    </BCard>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import ProductLookupSelect from './ProductLookupSelect.vue'
import ProductReviewCard from './ProductReviewCard.vue'
import { productReviewCheckerRegistry } from './product-review-checker-registry'
import {
    buildProductReviewViewModel,
    hasIdentity,
    normalizedProductId,
} from './product-review-view-model'

const clone = value => JSON.parse(JSON.stringify(value || []))

const normalizeProductSummary = summary => {
    const id = summary && (summary.id || summary.productId)
    if (!hasIdentity(id)) {
        return null
    }

    return {
        id: String(id),
        productId: String(id),
        name: summary.name || null,
        identifier: summary.identifier || null,
        raw: summary.raw || summary,
    }
}

export default {
    name: 'ProductsReviewSection',
    components: {
        BAlert,
        BButton,
        BCard,
        ProductLookupSelect,
        ProductReviewCard,
    },
    props: {
        products: { type: Array, default: () => [] },
        links: { type: Array, default: () => [] },
        violations: { type: Array, default: () => [] },
        reviewContext: { type: Object, default: () => ({}) },
        checkerRegistry: {
            type: Object,
            default: () => productReviewCheckerRegistry,
        },
        workspaceService: { type: Object, required: true },
        allowAddProduct: { type: Boolean, default: true },
        allowAddAttribute: { type: Boolean, default: true },
    },
    data() {
        return {
            summariesById: {},
            missingProductIds: [],
            hydratingIds: [],
            hydrationFailed: false,
            hydrationRevision: 0,
        }
    },
    computed: {
        productLookup() {
            const lookup = this.reviewContext && this.reviewContext.productLookup
            return lookup || {
                available: false,
                preferredProductId: null,
                search: () => Promise.resolve([]),
            }
        },
        preferredProductId() {
            return hasIdentity(this.productLookup.preferredProductId)
                ? String(this.productLookup.preferredProductId)
                : null
        },
        preferredSummary() {
            return this.preferredProductId
                ? this.summariesById[this.preferredProductId] || null
                : null
        },
        availablePreferredSummary() {
            return this.preferredSummary
                && this.viewModel.items.length === 0
                && !this.usedProductIds.includes(this.preferredProductId)
                ? this.preferredSummary
                : null
        },
        linkedProductIds() {
            return Array.from(new Set(this.links
                .map(normalizedProductId)
                .filter(Boolean)))
        },
        hydrationProductIds() {
            return Array.from(new Set(this.linkedProductIds.concat(
                this.preferredProductId ? [this.preferredProductId] : [],
            )))
        },
        hydrationProductIdsKey() {
            return this.hydrationProductIds.join(',')
        },
        usedProductIds() {
            return this.linkedProductIds
        },
        viewModel() {
            return buildProductReviewViewModel(
                this.products,
                this.links,
                this.summariesById,
                this.missingProductIds,
                this.checkerRegistry,
            )
        },
        summaryLabel() {
            const matched = this.viewModel.items.filter(item => (
                item.link && item.link.productId && item.link.status === 'accepted'
            )).length
            const template = this.translate(
                'Component(ResearchReviewProcess):products-summary',
                '{total} extracted · {matched} matched',
            )

            return template
                .replace('{total}', String(this.viewModel.items.length))
                .replace('{matched}', String(matched))
        },
    },
    watch: {
        hydrationProductIdsKey: {
            immediate: true,
            handler() {
                this.hydrateProducts()
            },
        },
    },
    beforeDestroy() {
        this.hydrationRevision += 1
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        emitState({ products = this.products, links = this.links }) {
            this.$emit('update', {
                products: clone(products),
                links: clone(links),
            })
        },
        addProduct() {
            if (!this.allowAddProduct) {
                return
            }

            this.emitState({
                products: this.products.concat({
                    extractedProductId: uuidv4(),
                    attributes: [{
                        type: 'product_name',
                        value: '',
                        evidence: null,
                    }],
                }),
            })
        },
        productViolations(productIndex) {
            const path = `[products][${productIndex}]`
            return this.violations.filter(violation => (
                violation && String(violation.path || '').includes(path)
            ))
        },
        updateProduct(index, product) {
            this.emitState({
                products: this.products.map((item, itemIndex) => (
                    itemIndex === index ? product : item
                )),
            })
        },
        removeProduct(index) {
            const product = this.products[index]
            const extractedProductId = product && product.extractedProductId
            const links = hasIdentity(extractedProductId)
                ? this.links.filter(link => !link
                    || !hasIdentity(link.extractedProductId)
                    || String(link.extractedProductId) !== String(extractedProductId))
                : this.links

            this.emitState({
                products: this.products.filter((item, itemIndex) => itemIndex !== index),
                links,
            })
        },
        selectLink(item, selected) {
            let { extractedProductId } = item.product
            let { products } = this

            if (!hasIdentity(extractedProductId)) {
                extractedProductId = uuidv4()
                products = this.products.map((product, index) => (
                    index === item.index ? { ...product, extractedProductId } : product
                ))
            }

            if (!selected) {
                const links = item.linkIndex === null
                    ? this.links
                    : this.links.filter((link, index) => index !== item.linkIndex)
                this.emitState({ products, links })
                return
            }

            const summary = normalizeProductSummary(selected)
            if (!summary || this.productIdUsedElsewhere(summary.id, item.linkIndex)) {
                return
            }

            this.$set(this.summariesById, summary.id, summary)
            const nextLink = {
                ...(item.link || {}),
                extractedProductId,
                productId: summary.id,
                type: 'manual',
                status: 'accepted',
                confidence: null,
                reason: null,
            }
            const links = item.linkIndex === null
                ? this.links.concat(nextLink)
                : this.links.map((link, index) => (
                    index === item.linkIndex ? nextLink : link
                ))

            this.emitState({ products, links })
        },
        addAdditionalLink(selected) {
            const summary = normalizeProductSummary(selected)
            if (!summary || this.usedProductIds.includes(summary.id)) {
                return
            }

            this.$set(this.summariesById, summary.id, summary)
            this.emitState({
                links: this.links.concat({
                    extractedProductId: null,
                    productId: summary.id,
                    type: 'manual',
                    status: 'accepted',
                    confidence: null,
                    reason: null,
                }),
            })
        },
        removeLink(sourceIndex) {
            this.emitState({
                links: this.links.filter((link, index) => index !== sourceIndex),
            })
        },
        productIdUsedElsewhere(productId, currentLinkIndex) {
            return this.links.some((link, index) => (
                index !== currentLinkIndex && normalizedProductId(link) === String(productId)
            ))
        },
        excludedProductIds(link) {
            const currentProductId = normalizedProductId(link)
            return this.usedProductIds.filter(productId => productId !== currentProductId)
        },
        recommendationFor(item) {
            if (!this.preferredSummary || normalizedProductId(item.link) === this.preferredProductId) {
                return null
            }

            return this.productIdUsedElsewhere(this.preferredProductId, item.linkIndex)
                ? null
                : this.preferredSummary
        },
        isHydrating(link) {
            const productId = normalizedProductId(link)
            return productId ? this.hydratingIds.includes(productId) : false
        },
        hydrateProducts() {
            const ids = this.hydrationProductIds.filter(id => !this.summariesById[id])
            this.hydrationRevision += 1
            const revision = this.hydrationRevision
            this.hydrationFailed = false

            if (!ids.length || typeof this.workspaceService.getProductSummaries !== 'function') {
                this.hydratingIds = []
                return Promise.resolve()
            }

            this.hydratingIds = ids
            return this.workspaceService.getProductSummaries(ids)
                .then(result => {
                    if (revision !== this.hydrationRevision) {
                        return
                    }

                    const items = Array.isArray(result.items) ? result.items : []
                    items.forEach(item => {
                        const summary = normalizeProductSummary(item)
                        if (summary) {
                            this.$set(this.summariesById, summary.id, summary)
                        }
                    })
                    const hydratedIds = items
                        .map(item => normalizeProductSummary(item))
                        .filter(Boolean)
                        .map(item => item.id)
                    const returnedMissingIds = Array.isArray(result.missingIds)
                        ? result.missingIds.map(String)
                        : []
                    this.missingProductIds = Array.from(new Set(this.missingProductIds
                        .filter(id => !ids.includes(String(id)))
                        .concat(returnedMissingIds.filter(id => !hydratedIds.includes(id)))))
                })
                .catch(() => {
                    if (revision === this.hydrationRevision) {
                        this.hydrationFailed = true
                    }
                })
                .finally(() => {
                    if (revision === this.hydrationRevision) {
                        this.hydratingIds = []
                    }
                })
        },
        productSummaryLabel(summary) {
            if (summary && summary.name && summary.identifier) {
                return `${summary.name} · ${summary.identifier}`
            }
            return (summary && (summary.name || summary.identifier))
                || `Product #${summary && (summary.id || summary.productId)}`
        },
        productLinkLabel(item) {
            return item.summary
                ? this.productSummaryLabel(item.summary)
                : `Product #${item.link.productId}`
        },
        linkStatusLabel(link) {
            const status = link && link.status ? link.status : 'uncertain'
            return this.translate(
                `Component(ResearchReviewProcessEnum):link-status-${status}`,
                status,
            )
        },
    },
}
</script>

<style lang="scss" scoped>
.products-review-section__heading {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.products-review-section__add {
    flex: 0 0 auto;
    margin-left: auto;
}

.products-review-section__additional,
.products-review-section__orphaned {
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
}

.products-review-section__preferred,
.products-review-section__link-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: .75rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.products-review-section__preferred {
    border-color: rgba(245, 128, 73, .3);
    background: rgba(245, 128, 73, .05);
}

.products-review-section__link-row {
    margin-top: .75rem;
}

.min-width-0 {
    min-width: 0;
}

@media (max-width: 767.98px) {
    .products-review-section__heading {
        flex-direction: column;
    }

    .products-review-section__add {
        margin-left: 0;
    }
}
</style>
